import { useCallback, useEffect, useMemo, useState } from "react";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { Accordion, Badge, Button as BsButton, Form, Modal } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { toast } from "react-toastify";
import { createApiService } from "./shared/apiConfig";
import {
  ContractsRuleApi,
  SubscriptionApi,
  SubscriptionGroupsApi,
} from "./shared/api/axios-client";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { ChevronDown, ChevronUp, Folder2Open, FolderPlus, Grid3x3Gap, InfoCircleFill, Pencil, Trash, XCircle } from "react-bootstrap-icons";
import { CostProjectionDto, CostProjectionSection } from "./CostProjectionSection";
import { mapCostProjection } from "./CostProjectionMapper";
import {
  InviteParkerForm,
  InviteParkerModal,
} from "./UserPlanDetailsPageInviteParkerModal";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { SubListEntry, SubscriptionUpdateReq, Vehicle } from "./Data/ApiTypes";
import ParkerEditModal from "./ParkerEditModal";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import { showMoney, toCents } from "./Data/Money";
import {
  usePendingChanges,
  useUnsavedChangesGuard,
} from "./PendingChangesContext";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { parseParkerInfo } from "./Data/ApiParse";
import { PageTitle } from "./Controls/PageTitle";
import { GroupModal } from "./GroupModal";
import { ActionDropdown, type ActionItem } from "./Controls/ActionDropdown";
import { ChangeParkerGroupModal } from "./ChangeParkerGroupModal";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import { CommitChangesConfirmDialog } from "./Controls/CommitChangesConfirmDialog";
import { useSubscriptionVehiclesAndIntegrations } from "./useSubscriptionVehiclesAndIntegrations";
import { ContractRuleModal, ContractRuleForm } from "./ContractRuleModal";
import TourGuide from "./Controls/TourGuide";
import "./SubscriptionManageGroupParkersPage.scss";
import { formatIsoDateShort, localDateToInstantUtc, showBrowserDate } from "./Data/Date";
import { LocalDate } from "@js-joda/core";
import {
  cancellationInstantToPickerDate,
  formatParkerCancelDate,
  getDefaultCancelParkerDate,
  getMinCancelParkerDate,
} from "./Data/cancelParker";
import { getInitials, avatarColor } from "./Data/Avatar";

enum Context {
  FromGroup,
  FromPlanGroups,
}

export const UNASSIGNED_GROUP_ID = 0;

export function SubscriptionManageGroupParkersPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  const [subscription, loadedGroups] = useLoaderData() as any;

  const groupId = searchParams.get("groupId")
    ? Number(searchParams.get("groupId"))
    : null;

  // URL groupId only sets the group filter selection; page is always plan view
  const context: Context = Context.FromPlanGroups;

  const [committedParkers, setCommittedParkers] = useState<any[]>([]);

  const [committedSpaces, setCommittedSpaces] = useState<number>(
    subscription.quantity,
  );
  const [editedSpaces, setEditedSpaces] = useState<number>(
    subscription.quantity,
  );

  const [allGuestParkers, setAllGuestParkers] = useState<any[]>([]);

  const [groupGuestParkers, setGroupGuestParkers] = useState<any[]>([]);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(0);

  const [search, setSearch] = useState("");
  const [showAddParker, setShowAddParker] = useState(false);

  const [showRemove, setShowRemove] = useState(false);
  const [removeParkerId, setRemoveParkerId] = useState<number | null>(null);
  const [cancelParkerDate, setCancelParkerDate] = useState("");
  const [cancelParkerImmediately, setCancelParkerImmediately] = useState(false);
  const [removeParkerFromGroup, setRemoveParkerFromGroup] = useState(false);
  const [showChangeGroup, setShowChangeGroup] = useState(false);
  const [selectedParker, setSelectedParker] = useState<any | null>(null);
  const [showEditParkerModal, setShowEditParkerModal] = useState(false);
  const [chooseModalVehicles, setChooseModalVehicles] = useState<Vehicle[]>([]);
  const [chooseModalExclude, setChooseModalExclude] = useState<number[]>([]);
  const [showChooseVehicle, setShowChooseVehicle] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groups, setGroups] = useState(loadedGroups);
  const [showAddContract, setShowAddContract] = useState(false);
  const [contractGroupId, setContractGroupId] = useState<number | null>(null);
  const [showCommitConfirmDialog, setShowCommitConfirmDialog] = useState(false);

  // Cost projection state
  const [currentProjection, setCurrentProjection] = useState<CostProjectionDto | null>(null);
  const [nextProjection, setNextProjection] = useState<CostProjectionDto | null>(null);
  const [costProjectionLoading, setCostProjectionLoading] = useState(false);
  const [hasFetchedCostProjection, setHasFetchedCostProjection] = useState(false);

  // Collapsible sections state
  const [spaceAllocationExpanded, setSpaceAllocationExpanded] = useState(true);

  const { hasChanges, setHasChanges, setHandlers } = usePendingChanges();
  const { showModal, stay, discard } = useUnsavedChangesGuard(
    hasChanges,
    () => {
      setAllGuestParkers(committedParkers);
      setHasChanges(false);
    },
  );
  const subGroupsApi = createApiService(SubscriptionGroupsApi);
  const contractsApi = createApiService(ContractsRuleApi);

  const { vehicleAdd, vehicleLink } = useSubscriptionVehiclesAndIntegrations({
    facilityId: subscription.facility.facilityId,
    parkerId: subscription.parkerId,
    subscriptionId: subscription.subscriptionId,
    vehicles: subscription.vehicles,
    setChooseModalVehicles,
    setChooseModalExclude,
    setShowChoose: setShowChooseVehicle,
    setParker: setSelectedParker,
    setParkers: setAllGuestParkers,
  });

  const isEffectiveCancellation = useCallback(
    (removedWhen?: string | null) => {
      if (!removedWhen) return false;

      return (
        cancellationInstantToPickerDate(
          removedWhen,
          subscription.facility.timeZone,
        ) <= getMinCancelParkerDate()
      );
    },
    [subscription.facility.timeZone],
  );

  useEffect(() => {
    if (!subscription?.parkers) return;

    const guests = subscription.parkers
      .map((guest: any) => ({
        subscriptionId: subscription.subscriptionId,
        sub: subscription,
        parkerId: guest.parker.parkerId,
        name: guest.parker.name,
        email: guest.parker.emailAddress,
        plan: subscription.plan.name,
        planId: subscription.plan.planId,
        isManaged: guest.parker.isManaged,
        quantity: guest.quantity,
        parker: guest.parker,
        vehicles: guest.vehicles,
        facilityId: subscription.plan.facilityId,
        groupId: guest.subscriptionGroupId,
        groupName: guest.subscriptionGroupName,
        startDate: guest.startDate,
        removedWhen: guest.removedWhen,
      }))
      .filter((guest: any) => !isEffectiveCancellation(guest.removedWhen));

    setCommittedParkers(guests);
    setAllGuestParkers(guests);

    setCommittedSpaces(subscription.quantity);
    setEditedSpaces(subscription.quantity);
  }, [subscription, isEffectiveCancellation]);

  useEffect(() => {
    if (groupId != null) {
      setSelectedGroupId(groupId);
    }
  }, [groupId]);

  useEffect(() => {
    let guests = [...allGuestParkers];

    if (selectedGroupId !== 0 && selectedGroupId != null) {
      guests = guests.filter((p) => p.isDraft || p.groupId === selectedGroupId);
    }

    if (search) {
      const searchTerm = search.toLowerCase();
      guests = guests.filter((p) => {
        const lpnText = getLpnText(p.vehicles).toLowerCase();
        return `${p.name} ${lpnText}`.toLowerCase().includes(searchTerm);
      });
    }

    setGroupGuestParkers(guests);
  }, [allGuestParkers, selectedGroupId, search]);

  const hasRealChanges = useMemo(() => {
    if (editedSpaces !== committedSpaces) return true;

    if (allGuestParkers.some((p) => p.isDraft)) return true;

    return allGuestParkers.some((p) => {
      const committed = committedParkers.find((c) => c.parkerId === p.parkerId);
      if (!committed) return false;
      return (
        committed.groupId !== p.groupId ||
        committed.removedWhen !== p.removedWhen
      );
    });
  }, [editedSpaces, committedSpaces, allGuestParkers, committedParkers]);

  useEffect(() => {
    setHasChanges(hasRealChanges);
  }, [hasRealChanges, setHasChanges]);

  const refreshCostProjection = async () => {
    setCostProjectionLoading(true);
    setHasFetchedCostProjection(true);
    try {
      const subscriptionApi = createApiService(SubscriptionApi);
      const response = (await subscriptionApi.subscriptionIdCostProjectionGet({ subscriptionId: subscription.subscriptionId })).data!;

      if (response.currentPeriod) {
        const mappedCurrent = mapCostProjection(
          response.currentPeriod,
          subscription.parkers,
        );
        setCurrentProjection(mappedCurrent);
      }

      if (response.nextPeriod) {
        const mappedNext = mapCostProjection(
          response.nextPeriod,
          subscription.parkers,
        );
        setNextProjection(mappedNext);
      }
    } finally {
      setCostProjectionLoading(false);
    }
  };

  // Auto-refresh cost projection if user has explicitly fetched data in this session
  useEffect(() => {
    if (!subscription.isEnhancedGroup) return;
    if (hasFetchedCostProjection) {
      refreshCostProjection();
    }
  }, [groups, allGuestParkers]);

  const isEditingContract =
    selectedGroupId != null &&
    (groups.find((x) => x.id == selectedGroupId)?.contractRules?.length ?? 0) > 0;

  const contractModalGroupId = contractGroupId ?? (selectedGroupId != null && selectedGroupId > 0 ? selectedGroupId : null);
  const isEditingContractInModal =
    contractModalGroupId != null &&
    (groups.find((x) => x.id === contractModalGroupId)?.contractRules?.length ?? 0) > 0;

  const modalGroups = groups
    .filter((x) => x.contractRules?.length === 0 || !x.contractRules)
    .map((g) => ({
      id: g.id!,
      name: g.name!,
    }));

  type ParkerRow = {
    parkerId: number;
    draftId?: string;
    isDraft: boolean;

    name: string;
    email: string;
    quantity: number;
    isManaged: boolean;

    parker: any;
    vehicles: Vehicle[];

    groupId: number | null;
    groupName: string | null;

    startDate?: string;
    removedWhen?: string | null;

    subscriptionId: number;
    lpn: string;
    plan: string;

    sub: {
      subscriptionId: number;
      status: SubscriptionStatus;
    };
  };

  const saveChanges = useCallback(async () => {
    if (editedSpaces !== committedSpaces) {
      const subscriptionUpdateReq: SubscriptionUpdateReq = {
        subscriptionId: subscription.subscriptionId,
        planPriceId: subscription.planPriceId!,
        quantity: editedSpaces,
        end: subscription.end,
        start: subscription.start,
        status: subscription.status,
        applyQuantityChangeNow: true,
        isAccountsReceivable: subscription.isAccountsReceivable,
        isLateFeeEnabled: true,
        subscriptionEntryId: 0,
      };

      await Api.facilitySubscriptionEditUpdate(
        subscription!.facility.facilityId,
        subscription.subscriptionId,
        subscriptionUpdateReq,
      ).then((res) =>
        processResult(
          res,
          () => {
            setCommittedSpaces(editedSpaces);
          },
          (err) => toast.error(err),
        ),
      );
    }

    const api = createApiService(SubscriptionGroupsApi);

    const groupChanges = allGuestParkers.filter(
      (p) =>
        !p.isDraft &&
        committedParkers.find((c) => c.parkerId === p.parkerId)?.groupId !==
          p.groupId,
    );

    for (const p of groupChanges) {
      await api.changeParkerGroup({
        serverRequestsParkerGroupChangeRequest: {
          parkerId: p.parkerId!,
          subscriptionGroupId: p.groupId,
          subscriptionId: subscription.subscriptionId,
        },
      });
    }

    const cancellationChanges = allGuestParkers.filter(
      (p) =>
        !p.isDraft &&
        committedParkers.find((c) => c.parkerId === p.parkerId)?.removedWhen !==
          p.removedWhen &&
        p.removedWhen,
    );

    for (const p of cancellationChanges) {
      const removedWhenInstant =
        typeof p.removedWhen === "string" && p.removedWhen.includes("T")
          ? p.removedWhen
          : localDateToInstantUtc(p.removedWhen);

      const ok = await Api.subParkerRemove(
        subscription.facility.facilityId,
        subscription.subscriptionId,
        { parkerId: p.parkerId, removedWhen: removedWhenInstant },
      ).then((resp) =>
        processResult(
          resp,
          () => true,
          (err) => {
            toast.error(err);
            return false;
          },
        ),
      );

      if (!ok) return;
    }

    const drafts = allGuestParkers.filter((p) => p.isDraft);
    const created: ParkerRow[] = [];
    let batchSucceeded = true;

    if (drafts.length > 0) {
      const reqs = drafts.map((d) => ({
        name: d.name,
        emailAddress: d.email,
        subscriptionGroupId: d.groupId,
        quantity: 1,
        isLoginEnabled: false,
        subscriptionId: subscription.subscriptionId,
        startDate: localDateToInstantUtc(d.startDate),
        pendingIntegrationCredential: d.pendingIntegrationCredential ?? null,
      }));
      const resp = await Api.subParkerBatchInvite(
        subscription.facility.facilityId,
        subscription.subscriptionId,
        reqs,
      );
      const batchOk = processResult(resp, (list) => {
        list.forEach((val, i) => {
          const d = drafts[i];
          created.push({
            parkerId: val.parker.parkerId,
            isDraft: false,

            name: val.parker.name,
            email: val.parker.emailAddress,
            quantity: val.quantity,
            isManaged: val.parker.isManaged,

            parker: parseParkerInfo(val.parker),
            vehicles: [],

            groupId: d.groupId,
            groupName:
              d.groupId != null
                ? (groups!.find((g) => g.id === d.groupId)?.name ?? null)
                : null,

            startDate: val.startDate,

            subscriptionId: 0,
            lpn: "N/A",
            plan: "",

            sub: {
              subscriptionId: 0,
              status: SubscriptionStatus.Active,
            },
          });
        });
      }, (err) => toast.error(typeof err === "string" ? err : (err as { message?: string })?.message ?? "Failed to add parkers"));
      batchSucceeded = batchOk;
    }

    const spacesChanged = editedSpaces !== committedSpaces;
    const parkersAdded = drafts.length > 0;
    const parkersRemoved = committedParkers.some(
      (c) => !allGuestParkers.find((p) => p.parkerId === c.parkerId && !p.isDraft)
    );
    const parkersChangedGroups = groupChanges.length > 0;
    const parkersCancelled = cancellationChanges.length > 0;

    if (spacesChanged || parkersAdded || parkersRemoved || parkersChangedGroups || parkersCancelled) {
      await Api.subscriptionUpdateEnhanced(
        subscription.facility.facilityId,
        subscription.subscriptionId
      ).then((enhancedRes) =>
        processResult(
          enhancedRes,
          () => {},
          (err) => toast.error(err)
        )
      );
    }

    const committed = [
      ...allGuestParkers.filter(
        (p) => !p.isDraft && !isEffectiveCancellation(p.removedWhen),
      ),
      ...created,
    ];

    setAllGuestParkers(committed);
    setCommittedParkers(committed);
    setHasChanges(false);

    if (batchSucceeded) {
      toast.success("Changes saved successfully");
    } else {
      toast.error("Failed to add some parkers. Please try again.");
    }
  }, [
    editedSpaces,
    committedSpaces,
    allGuestParkers,
    committedParkers,
    subscription.subscriptionId,
    setHasChanges,
    isEffectiveCancellation,
  ]);

  const cancelChanges = useCallback(() => {
    setAllGuestParkers([...committedParkers]);
    setEditedSpaces(committedSpaces);
    setSelectedParker(null);
    setShowCancelConfirm(false);
    setHasChanges(false);
  }, [committedParkers, committedSpaces]);

  useEffect(() => {
    setHandlers({
      onSave: () => setShowCommitConfirmDialog(true),
      onDiscard: () => setShowCancelConfirm(true),
    });
  }, [setHandlers, cancelChanges]);

  const getLpnText = (vehicles: Vehicle[]) =>
    vehicles?.length
      ? vehicles.map((v) => v.licensePlateNumber).join(" | ")
      : "N/A";

  const getPendingRowStyle = (p: { isDraft?: boolean; parkerId?: number }, defaultColor?: string) => {
    const committed = p.parkerId
      ? committedParkers.find((c) => c.parkerId === p.parkerId)
      : undefined;
    const isPending =
      p.isDraft ||
      (committed != null &&
        allGuestParkers.some(
          (guest) =>
            guest.parkerId === p.parkerId &&
            (guest.groupId !== committed.groupId ||
              guest.removedWhen !== committed.removedWhen),
        ));

    return {
      backgroundColor: isPending ? "rgb(255 248 248)" : defaultColor,
    };
  };

  async function assignParkerToGroup(
    parker: { parkerId?: number; draftId?: string; isDraft: boolean },
    groupId: number | null,
  ) {
    setAllGuestParkers((prev) =>
      prev.map((p) => {
        const isSame =
          p.isDraft && parker.isDraft
            ? p.draftId === parker.draftId
            : !p.isDraft && !parker.isDraft
              ? p.parkerId === parker.parkerId
              : false;

        if (!isSame) return p;

        return {
          ...p,
          groupId,
          groupName:
            groupId == null
              ? null
              : (groups.find((g) => g.id === groupId)?.name ?? null),
        };
      }),
    );
  }

  async function createGroup(name: string) {
    const res = await subGroupsApi.addGroup({
      serverRequestsSubscriptionGroupAddRequest: {
        subscriptionId: subscription.subscriptionId,
        name,
      },
    });

    const group = res.data;

    if (group?.error) {
      toast.error(group.error);
      return;
    }

    setGroups((prev) => [
      ...prev,
      {
        id: group.id,
        name: group.name,
        parkerCount: 0,
      },
    ]);

    toast.success("Group created successfully.");

    return group;
  }

  const totalSpaces = editedSpaces;
  const totalParkers = allGuestParkers.length;

  const unallocatedSpaces = totalSpaces - totalParkers;

  const unassignedCount = useMemo(
    () => allGuestParkers.filter((p) => !p.groupId).length,
    [allGuestParkers],
  );

  const hasAvailableSpaces = unallocatedSpaces > 0;

  const hasUnassigned =
    unassignedCount > 0;
  const hasUnallocated =
    unallocatedSpaces > 0;

  const showWarning = hasUnassigned || hasUnallocated;

  const currentGroupName =
    selectedGroupId != null && selectedGroupId > 0
      ? groups.find((g) => g.id === selectedGroupId)?.name ?? null
      : null;

  async function parkerInvite(
    data: InviteParkerForm,
    groupId?: number | null,
    plan?: SubListEntry,
    shouldCommit?: boolean,
  ) {
    const commit = shouldCommit ?? true;

    if (!commit) {
      setAllGuestParkers((prev) => [
        ...prev,
        {
          draftId: crypto.randomUUID(),
          isDraft: true,
          name: data.name,
          email: data.emailAddress,
          startDate: data.startDate,
          pendingIntegrationCredential: data.pendingIntegrationCredential,
          groupId,
          vehicles: [],
          groupName:
            groupId != null
              ? (groups!.find((g) => g.id === groupId)?.name ?? null)
              : null,
        },
      ]);

      return;
    }

    const subscriptionGroupId = groupId ?? null;

    const populatedSub = subscription.subscriptioId || plan!.subscriptionId;
    const resp = await Api.subParkerInvite(
      subscription.facility.facilityId,
      populatedSub,
      {
        emailAddress: data.emailAddress,
        name: data.name,
        quantity: 1,
        subscriptionId: populatedSub,
        isLoginEnabled: data.isLoginEnabled,
        subscriptionGroupId,
        startDate: localDateToInstantUtc(data.startDate),
        pendingIntegrationCredential: data.pendingIntegrationCredential,
      },
    );

    processResult(
      resp,
      (val) => {
        toast.success("Parker Added Successfully");

        setAllGuestParkers((prev) => [
          ...prev,
          {
            populatedSub,
            parkerId: val.parker.parkerId,
            name: val.parker.name,
            email: val.parker.emailAddress,
            quantity: val.quantity,
            isManaged: val.parker.isManaged,
            parker: parseParkerInfo(val.parker),
            vehicles: [],
            groupId: subscriptionGroupId,
            groupName:
              subscriptionGroupId != null
                ? (groups!.find((g) => g.id === subscriptionGroupId)?.name ??
                  null)
                : null,
            startDate: val.startDate,
            subscriptionId: populatedSub,
            lpn: "N/A",
            plan: plan?.plan.name || "",
            sub: {
              subscriptionId: populatedSub,
              status: SubscriptionStatus.Active,
            },
            isDraft: false,
          },
        ]);
      },
      (err) => {
        toast.error(err);
      },
    );
  }

  const getCurrentAccessPeriodEndDate = () => {
    const dates = (subscription.accessPeriods ?? [])
      .map((ap: any) => String(ap.end))
      .filter(Boolean)
      .sort();

    return dates.at(-1) ?? showBrowserDate(LocalDate.now());
  };

  const getParkerRemoveActions = (p: any, iconSize = 16): ActionItem[] => {
    const removeIcon = (
      <Trash size={iconSize} className="text-muted mb-1" />
    );
    const cancelIcon = (
      <XCircle size={iconSize} className="text-muted mb-1" />
    );

    if (p.isDraft) {
      return [{
        label: p.groupId ? "Remove from Group" : "Remove Parker",
        icon: removeIcon,
        onClick: () => {
          if (!p.draftId) return;

          if (p.groupId) {
            setAllGuestParkers((prev) =>
              prev.map((x) =>
                x.draftId === p.draftId
                  ? { ...x, groupId: null, groupName: null }
                  : x,
              ),
            );
            return;
          }

          setAllGuestParkers((prev) =>
            prev.filter((x) => x.draftId !== p.draftId),
          );
        },
      }];
    }

    const actions: ActionItem[] = [];

    if (p.groupId) {
      actions.push({
        label: "Remove from Group",
        icon: removeIcon,
        onClick: () => {
          setRemoveParkerId(p.parkerId);
          setRemoveParkerFromGroup(true);
        },
      });
    }

    actions.push({
      label: "Cancel Parker",
      icon: cancelIcon,
      onClick: () => {
        const defaultCancelDate = getDefaultCancelParkerDate({
          removedWhen: p.removedWhen,
          accessPeriodEnd: getCurrentAccessPeriodEndDate(),
          facilityTimeZone: subscription.facility.timeZone,
        });

        setRemoveParkerId(p.parkerId);
        setCancelParkerDate(defaultCancelDate);
        setCancelParkerImmediately(false);
        setShowRemove(true);
      },
    });

    return actions;
  };

  function stageParkerCancellation(parkerId: number, removedWhen: string) {
    const removedWhenInstant = localDateToInstantUtc(removedWhen);
    setAllGuestParkers((prev) =>
      prev.map((p) =>
        p.parkerId === parkerId
          ? { ...p, removedWhen: removedWhenInstant }
          : p,
      ),
    );
  }

  async function saveContract(data: {
    groupId: number;
    rules: ContractRuleForm[];
  }) {
    // REMOVE UI-ONLY FIELDS
    const cleanRules = (data.rules as any[]).map(
      ({ _priceText, _isPriceFocused, id, ...rule }) => rule,
    );

    const res = await contractsApi.contractsRulePost({
      serverRequestsContractsRuleSaveRequest: {
        subscriptionGroupId: data.groupId,
        rules: cleanRules,
      },
    });

    const result = res.data;

    if (result?.error) {
      toast.error(result.error);
      return;
    }
    setGroups((prev) =>
      prev.map((g) =>
        g.id === data.groupId
          ? {
              ...g,
              contractRules: result.rules ?? [],
            }
          : g,
      ),
    );

    toast.success("Contract created successfully.");
    return result;
  }

  async function removeContract(groupId: number) {
    try {
      const res = await contractsApi.contractsRuleSubscriptionGroupIdDelete({
        subscriptionGroupId: groupId,
      });

      const result = res.data;

      if (result?.error) {
        toast.error(result.error);
        return;
      }

      setGroups((prev) =>
        prev.map((g) =>
          g.id === groupId
            ? {
                ...g,
                contractRules: [],
                hasContract: false,
              }
            : g,
        ),
      );

      toast.success("Contract removed successfully.");
    } catch (err) {
      toast.error("Failed to remove contract.");
    }
  }

  return (
    <div className="container-fluid py-3 d-flex flex-column h-100">
      <div className="mb-2 step-manage-intro">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <BsButton
              variant="link"
              className="p-0 mb-2 text-decoration-none text-danger"
              onClick={() => nav(-1)}
            >
              ← Back to Plan
            </BsButton>

            <PageTitle>Manage Plan</PageTitle>

            <div className="d-flex gap-2 fs-5 fw-semibold text-muted align-items-center">
              {currentGroupName && (
                <>
                  <Badge bg="secondary">{currentGroupName}</Badge>
                </>
              )}

              <span>{subscription.plan.name}</span>
            </div>
          </div>
        </div>
      </div>

      {showWarning && (
        <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3">
          <InfoCircleFill color="warning" className="mt-1" />

          <div>
            <div className="fw-semibold mb-1">Attention Required</div>

            <div className="small mb-2">
              The following will be charged the base rate of{" "}
              <strong>{showMoney(subscription.price)}</strong>:
            </div>

            <ul className="mb-0 ps-3 small">
              {hasUnassigned && (
                <li>
                  <strong>{unassignedCount}</strong> parker
                  {unassignedCount > 1 ? "s are" : " is"} not assigned to a
                  group
                </li>
              )}

              {hasUnallocated && (
                <li>
                  <strong>{unallocatedSpaces}</strong> unallocated space
                  {unallocatedSpaces > 1 ? "s" : ""}
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      <Accordion
          className="mb-3 cost-projection-accordion"
          activeKey={spaceAllocationExpanded ? "0" : undefined}
          onSelect={(eventKey) => setSpaceAllocationExpanded(eventKey === "0")}
        >
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <div className="d-flex align-items-center gap-2 w-100 me-3">
                {spaceAllocationExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                <Grid3x3Gap size={16} className="text-muted" />
                <span>{spaceAllocationExpanded ? "Hide" : "Show"} Space Allocation</span>
              </div>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="card border-0">
                <div className="card-body py-2">
                  <div className="row text-center align-items-stretch space-allocation-row">
                    <div className="col d-flex flex-column align-items-center">
                      <div className="text-muted small mb-2">TOTAL SPACES</div>

                      <input
                        type="number"
                        min={totalParkers}
                        value={editedSpaces}
                        onChange={(e) => setEditedSpaces(Number(e.target.value))}
                        className={"space-input"}
                      />

                      <div className="small text-muted mt-2">Editable</div>
                    </div>

                    <div className="col-auto px-0">
                      <div className="vertical-divider" />
                    </div>

                    <div className="col d-flex flex-column align-items-center">
                      <div className="text-muted small mb-2">TOTAL PARKERS</div>
                      <div className="fw-bold fs-3">{totalParkers}</div>
                    </div>

                    <div className="col-auto px-0">
                      <div className="vertical-divider" />
                    </div>

                    <div className="col d-flex flex-column align-items-center">
                      <div className="text-muted small mb-2">UNALLOCATED SPACES</div>

                      <div className="fw-bold fs-3 text-unallocated">
                        {unallocatedSpaces}
                      </div>

                      {unallocatedSpaces > 0 && (
                        <div className="small text-unallocated mt-1">
                          Base rate applies
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

      {subscription.isEnhancedGroup && (
        <CostProjectionSection
          current={currentProjection}
          next={nextProjection}
          onLoad={refreshCostProjection}
          subscriptionStartDate={subscription.start?.toString?.() ?? undefined}
          isAccountsReceivable={subscription.isAccountsReceivable}
        />
      )}

      <div
        className="card border-0 shadow-none flex-grow-1 overflow-auto step-parkers-card"
        style={{
          paddingBottom: hasChanges ? 60 : 0,
        }}
      >
        <div className="card-header border-0 bg-white py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 parkers-header-row">
            <h5 className="mb-0 parkers-header-title">
              {groupGuestParkers.length} Parker
              {groupGuestParkers.length !== 1 ? "s" : ""}
            </h5>

            <div className="d-flex gap-2 align-items-center step-actions">
              {modalGroups.length > 0 && (
                <Button
                  size="sm"
                  className="btn-main"
                  onClick={() => setShowAddContract(true)}
                >
                  {isEditingContract ? "Edit Contract" : "Add Contract"}
                </Button>
              )}
              <Button
                size="sm"
                className={`btn-main ${
                  !hasAvailableSpaces ? "opacity-50" : ""
                }`}
                onClick={() => {
                  if (!hasAvailableSpaces) {
                    toast.error(
                      "No more spaces left, you need more to add a new parker",
                    );
                    return;
                  }
                  setShowAddParker(true);
                }}
              >
                Add Parker
              </Button>

              {subscription.status === SubscriptionStatus.Active && (
                  <Button
                    size="sm"
                    className="btn-main "
                    onClick={() => setShowGroupModal(true)}
                  >
                    Add Group
                  </Button>
                )}
            </div>
          </div>

          <div className="step-filters d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 py-2 rounded border border-light ">
            <select
                className="form-select form-select-sm step-filters-select"
                style={{ width: "auto", minWidth: "140px" }}
                value={selectedGroupId === null ? -1 : (selectedGroupId ?? 0)}
                onChange={(e) => {
                  const raw = Number(e.target.value);
                  if (raw === -1) setSelectedGroupId(null);
                  else setSelectedGroupId(raw);
                }}
              >
                <option value={0}>All Groups</option>
                <option value={-1}>Unassigned</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            <input
              className="form-control form-control-sm flex-grow-1 step-filters-search"
              style={{ maxWidth: "280px" }}
              placeholder="Search by name or LPN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              size="sm"
              variant="outline-secondary"
              className="text-nowrap step-filters-clear"
              onClick={() => {
                setSelectedGroupId(0);
                setSearch("");
              }}
            >
              Clear
            </Button>
          </div>

          {hasRealChanges && (
            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mt-3 mb-0">
              <div className="fs-5 lh-1">⚠️</div>
              <div>
                <div className="fw-semibold mb-1">Changes pending save</div>
                <div className="small">Changes won't take effect until you save.</div>
              </div>
            </div>
          )}
        </div>

        <TourGuide tourKey="subscriptionManageGroupParkers" />

        <div className="card d-none d-md-block">
          {groupGuestParkers.length > 0 ? (
          <div className="table-responsive">
            <table className="table mb-0 align-middle">
              <thead className="table-light">
                <tr className="small text-muted">
                  <th className="py-2 fw-normal">Name</th>
                  <th className="py-2 fw-normal">Group</th>
                  <th className="py-2 fw-normal">LPN</th>
                  <th className="py-2 fw-normal">Start Date</th>
                  <th className="py-2 fw-normal">Cancel Date</th>
                  <th className="text-end py-2 fw-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupGuestParkers.map((p) => (
                  <tr key={p.parkerId || p.draftId}>
                    <td
                      className="fw-semibold py-3"
                      style={getPendingRowStyle(p)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <div className="position-relative flex-shrink-0">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                            style={{
                              width: 36,
                              height: 36,
                              backgroundColor: avatarColor(p.name),
                              fontSize: "0.8rem",
                            }}
                          >
                            {getInitials(p.name)}
                          </div>
                          {p.groupId ? (
                            <span
                              className="position-absolute rounded-circle border border-2 border-white"
                              style={{
                                width: 10,
                                height: 10,
                                backgroundColor: "#d63384",
                                bottom: -1,
                                right: -1,
                              }}
                              title="Assigned to group"
                            />
                          ) : (
                            <span
                              className="position-absolute rounded-circle border border-2 border-white"
                              style={{
                                width: 10,
                                height: 10,
                                backgroundColor: "#adb5bd",
                                bottom: -1,
                                right: -1,
                              }}
                              title="Unassigned"
                            />
                          )}
                        </div>
                        <span className="text-primary">{p.name}</span>
                      </div>
                    </td>
                    <td className="py-3" style={getPendingRowStyle(p)}>
                      {p.groupName ? (
                        <button
                          type="button"
                          className="badge rounded-pill bg-danger-subtle text-danger px-3 py-2 border-0 text-decoration-none"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setContractGroupId(p.groupId!);
                            setShowAddContract(true);
                          }}
                          title="View or edit contract"
                        >
                          {p.groupName}
                        </button>
                      ) : (
                        <span className="badge rounded-pill bg-light text-muted px-3 py-2">
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td style={getPendingRowStyle(p)}>
                      {getLpnText(p.vehicles)}
                    </td>
                    <td className="py-3" style={getPendingRowStyle(p)}>
                      {formatIsoDateShort(p.startDate)}
                    </td>
                    <td className="py-3" style={getPendingRowStyle(p)}>
                      {formatParkerCancelDate(
                        p.removedWhen,
                        subscription.facility.timeZone
                      )}
                    </td>

                    <td
                      className="text-end py-3 pe-4"
                      style={getPendingRowStyle(p)}
                    >
                      <div className="d-inline-flex gap-2">
                        <ActionDropdown
                          actions={[
                            {
                              label: p.groupId
                                ? "Change Group"
                                : "Assign to Group",
                              icon: p.groupId ? (
                                <Folder2Open
                                  size={16}
                                  className="text-muted mb-1"
                                />
                              ) : (
                                <FolderPlus
                                  size={16}
                                  className="text-muted mb-1"
                                />
                              ),
                              onClick: () => {
                                setSelectedParker(p);
                                setShowChangeGroup(true);
                              },
                            },
                            ...(!p.isDraft
                              ? [
                                  {
                                    label: "Edit Parker",
                                    icon: (
                                      <Pencil
                                        size={16}
                                        className="text-muted cursor-pointer mb-1"
                                      />
                                    ),
                                    onClick: () => {
                                      setSelectedParker(p);
                                      setShowEditParkerModal(true);
                                    },
                                  },
                                ]
                              : []),
                            ...getParkerRemoveActions(p),
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 border-0">
              <div
                className="rounded-circle mb-3"
                style={{
                  width: 72,
                  height: 72,
                  backgroundColor: "rgba(220, 53, 69, 0.15)",
                }}
              />
              <p className="text-secondary mb-4">
                Add parkers to get started
              </p>
              <Button
                className={`btn-main px-4 ${!hasAvailableSpaces ? "opacity-50" : ""}`}
                onClick={() => {
                  if (!hasAvailableSpaces) {
                    toast.error(
                      "No more spaces left, you need more to add a new parker",
                    );
                    return;
                  }
                  setShowAddParker(true);
                }}
              >
                Add Parker
              </Button>
            </div>
          )}
        </div>

        <div
          className="d-block d-md-none px-3 mt-2"
          style={{ flex: 1, overflowY: "auto" }}
        >
          {groupGuestParkers.length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
              <div
                className="rounded-circle mb-3"
                style={{
                  width: 72,
                  height: 72,
                  backgroundColor: "rgba(220, 53, 69, 0.15)",
                }}
              />
              <p className="text-secondary mb-4">
                Add parkers to this plan to get started
              </p>
              <Button
                className={`btn-main px-4 ${!hasAvailableSpaces ? "opacity-50" : ""}`}
                onClick={() => {
                  if (!hasAvailableSpaces) {
                    toast.error(
                      "No more spaces left, you need more to add a new parker",
                    );
                    return;
                  }
                  setShowAddParker(true);
                }}
              >
                Add Parker
              </Button>
            </div>
          ) : (
            groupGuestParkers.map((p) => (
              <div
                key={p.parkerId || p.draftId}
                className="card mb-3 rounded-4 border-1"
                style={getPendingRowStyle(p, "white")}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="position-relative flex-shrink-0">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-white"
                        style={{ width: 44, height: 44, backgroundColor: avatarColor(p.name), fontSize: "0.85rem" }}
                      >
                        {getInitials(p.name)}
                      </div>
                      {p.groupId ? (
                        <span
                          className="position-absolute rounded-circle border border-2 border-white"
                          style={{
                            width: 10,
                            height: 10,
                            backgroundColor: "#d63384",
                            bottom: -1,
                            right: -1,
                          }}
                          title="Assigned to group"
                        />
                      ) : (
                        <span
                          className="position-absolute rounded-circle border border-2 border-white"
                          style={{
                            width: 10,
                            height: 10,
                            backgroundColor: "#adb5bd",
                            bottom: -1,
                            right: -1,
                          }}
                          title="Unassigned"
                        />
                      )}
                    </div>
                    <div className="flex-grow-1 min-width-0">
                      <div className="fw-bold">{p.name}</div>
                      <div className="d-flex align-items-center gap-2 mt-1">
                        {p.groupName ? (
                          <button
                            type="button"
                            className="badge rounded-pill bg-danger-subtle text-danger border-0 text-decoration-none"
                            style={{ cursor: "pointer", fontSize: "0.7rem" }}
                            onClick={() => {
                              setContractGroupId(p.groupId!);
                              setShowAddContract(true);
                            }}
                            title="View or edit contract"
                          >
                            {p.groupName}
                          </button>
                        ) : (
                          <span className="badge rounded-pill bg-light text-muted" style={{ fontSize: "0.7rem" }}>
                            Unassigned
                          </span>
                        )}
                      </div>
                      <div className="text-muted small mt-1">Start Date: <span className="fw-semibold">{formatIsoDateShort(p.startDate)}</span></div>
                      <div className="text-muted small mt-1">Cancel Date: <span className="fw-semibold">{formatParkerCancelDate(p.removedWhen, subscription.facility.timeZone)}</span></div>
                    </div>
                    <ActionDropdown
                      actions={[
                        {
                          label: p.groupId ? "Change Group" : "Assign to Group",
                          icon: p.groupId ? (
                            <Folder2Open
                              size={18}
                              className="text-muted mb-1"
                            />
                          ) : (
                            <FolderPlus size={18} className="text-muted mb-1" />
                          ),
                          onClick: () => {
                            setSelectedParker(p);
                            setShowChangeGroup(true);
                          },
                        },
                        ...(!p.isDraft
                          ? [
                              {
                                label: "Edit Parker",
                                icon: (
                                  <Pencil
                                    size={16}
                                    className="text-muted cursor-pointer mb-1"
                                  />
                                ),
                                onClick: () => {
                                  setSelectedParker(p);
                                  setShowEditParkerModal(true);
                                },
                              },
                            ]
                          : []),
                        ...getParkerRemoveActions(p, 18),
                      ]}
                    />
                  </div>
                </div>
                <div className="card-footer bg-white d-flex align-items-center flex-wrap gap-2 py-2">
                  <span className="text-muted small text-uppercase fw-semibold me-1">Plates</span>
                  {(p.vehicles?.length ?? 0) === 0 ? (
                    <span className="text-muted small fst-italic">None on file</span>
                  ) : (
                    <>
                      <Badge pill bg="light" className="text-dark border d-inline-flex align-items-center gap-1">
                        {p.vehicles[0].licensePlateNumber}
                      </Badge>
                      {p.vehicles.length > 1 && (
                        <Badge pill bg="light" className="text-muted border">
                          +{p.vehicles.length - 1} more
                        </Badge>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {showChangeGroup && selectedParker && (
          <ChangeParkerGroupModal
            show={showChangeGroup}
            onClose={() => {
              setShowChangeGroup(false);
              setSelectedParker(null);
            }}
            parkerName={selectedParker.name}
            currentGroupId={selectedParker.groupId}
            groups={groups}
            onConfirm={(groupId) => {
              assignParkerToGroup(selectedParker, groupId);
            }}
          />
        )}
        {showEditParkerModal && selectedParker && (
          <ParkerEditModal
            show={showEditParkerModal}
            onClose={() => {
              setShowEditParkerModal(false);
              setSelectedParker(null);
            }}
            parker={selectedParker}
            setParker={setSelectedParker}
            parkers={allGuestParkers}
            setParkers={setAllGuestParkers}
            facilityId={subscription.facility.facilityId}
            selectedParkerSub={subscription}
          />
        )}

        <ConfirmModal
          title="Remove Parker From Group"
          message="Are you sure you want to remove this parker from the group?"
          show={removeParkerFromGroup}
          setShow={setRemoveParkerFromGroup}
          onConfirm={() => {
            const parker = allGuestParkers.find(
              (p) => !p.isDraft && p.parkerId === removeParkerId,
            );
            if (parker) {
              assignParkerToGroup(parker, null);
            }
          }}
        />

        <AddVehicleModal
          show={showAddVehicle}
          setShow={setShowAddVehicle}
          onSubmit={vehicleAdd}
        />

        <ChooseVehicleModal
          show={showChooseVehicle}
          setShow={setShowChooseVehicle}
          onSubmit={vehicleLink}
          vehicles={chooseModalVehicles}
          exclude={chooseModalExclude}
          onClose={() => setShowChooseVehicle(false)}
        />

        <InviteParkerModal
          show={showAddParker}
          setShow={setShowAddParker}
          facilityId={subscription.facility.facilityId}
          isFromGroupContext={true}
          defaultSubscriptionGroupId={
            selectedGroupId != null && selectedGroupId > 0
              ? selectedGroupId
              : UNASSIGNED_GROUP_ID
          }
          subscriptionStart={subscription.start}
          submit={async (data: InviteParkerForm) => {
            const normalizedGroupId =
              data.subscriptionGroupId === UNASSIGNED_GROUP_ID
                ? null
                : data.subscriptionGroupId;
            parkerInvite(
              data,
              normalizedGroupId ?? undefined,
              undefined,
              false,
            );
            setShowAddParker(false);
          }}
          groups={groups}
          isEnhancedGroup={true}
        />

        <Modal centered show={showRemove} onHide={() => setShowRemove(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Cancel Parker</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3">Are you sure you want to cancel this parker?</p>
            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3">
              <InfoCircleFill className="flex-shrink-0 mt-1" />
              <div>
                <span>
                  Access continues through the end of the subscription period.              
                  Any applicable credits must be issued manually.
                </span>
                
              </div>
            </div>
            <Form.Check
              className="mb-3"
              type="checkbox"
              id="cancel-group-parker-immediately"
              label="Cancel immediately"
              checked={cancelParkerImmediately}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setCancelParkerImmediately(isChecked);

                if (isChecked) {
                  setCancelParkerDate(getMinCancelParkerDate());
                  return;
                }

                const parker = allGuestParkers.find(
                  (p) => p.parkerId === removeParkerId,
                );
                setCancelParkerDate(
                  parker
                    ? getDefaultCancelParkerDate({
                        removedWhen: parker.removedWhen,
                        accessPeriodEnd: getCurrentAccessPeriodEndDate(),
                        facilityTimeZone: subscription.facility.timeZone,
                      })
                    : getMinCancelParkerDate(),
                );
              }}
            />
            {!cancelParkerImmediately && (
              <Form.Group className="mb-0">
                <Form.Label>Cancellation date</Form.Label>
                <Form.Control
                  type="date"
                  min={getMinCancelParkerDate()}
                  value={cancelParkerDate}
                  onChange={(e) => setCancelParkerDate(e.target.value)}
                />
              </Form.Group>
            )}
            <div className="small text-muted mt-2 mb-0">
                  This change will not take effect until you commit the changes.
                </div>
          </Modal.Body>
          <Modal.Footer>
            <BsButton variant="outline-secondary" onClick={() => setShowRemove(false)}>
              Go Back
            </BsButton>
            <BsButton
              variant="danger"
              onClick={() => {
                if (removeParkerId !== undefined && cancelParkerDate) {
                  stageParkerCancellation(removeParkerId!, cancelParkerDate);
                }
                setShowRemove(false);
              }}
            >
              Confirm Cancellation
            </BsButton>
            
          </Modal.Footer>
        </Modal>

        <ConfirmModal
          title="Discard changes?"
          message="If you cancel now, all unsaved changes will be lost."
          confirmText="Discard"
          show={showCancelConfirm}
          setShow={setShowCancelConfirm}
          onConfirm={cancelChanges}
        />

        <UnsavedChangesModal
          show={showModal}
          onStay={stay}
          onDiscard={discard}
        />
        <CommitChangesConfirmDialog
          show={showCommitConfirmDialog}
          onClose={() => setShowCommitConfirmDialog(false)}
          onConfirm={async () => {
            setShowCommitConfirmDialog(false);
            await saveChanges();
          }}
          isInvoiced={subscription.isAccountsReceivable}
          isSubscriptionActive={subscription.status === SubscriptionStatus.Active}
        />
        <GroupModal
          show={showGroupModal}
          onClose={() => setShowGroupModal(false)}
          existingGroups={groups}
          onSubmit={async (name, _groupId, options) => {
            const createdGroup = await createGroup(name);
            if (
              createdGroup &&
              options?.addContractAfterSave &&
              createdGroup.id != null
            ) {
              setContractGroupId(createdGroup.id);
              setShowAddContract(true);
            }
          }}
        />

        {showAddContract && (
          <ContractRuleModal
            show={showAddContract}
            onClose={() => { setShowAddContract(false); setContractGroupId(null); }}
            context={contractModalGroupId != null ? { mode: "group", groupId: contractModalGroupId } : { mode: "plan" }}
            basePriceCents={toCents(subscription.price)}
            onSubmit={async (data) => {
              await saveContract(data);
            }}
            isEditing={isEditingContractInModal}
            groups={modalGroups}
            initialRules={contractModalGroupId != null ? groups.find((x) => x.id === contractModalGroupId)?.contractRules : undefined}
            onRemoveContract={removeContract}
          />
        )}
      </div>
    </div>
  );
}
